import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useAgents } from "@/components/Agents/AgentsContext";
import { useSession } from "@/auth/SessionProvider";
import { agentDirectory, defaultAgentOwnerId } from "@/lib/agentsDemoData";
import { agentsHref } from "@/lib/atwAgentsHost";

export function CreateAgentPage() {
  const { org, may, viewOnly, hireAgent } = useAgents();
  const session = useSession();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [ownerChoice, setOwnerChoice] = useState<string | null>(null);
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const canCreate = may("create_edit_archive_agents");
  const people = useMemo(
    () => agentDirectory(org.users, session.data?.user),
    [org.users, session.data?.user],
  );
  const ownerId =
    ownerChoice && people.some((person) => person.id === ownerChoice)
      ? ownerChoice
      : defaultAgentOwnerId(people, session.data?.user);
  const shareOptions = people.filter((person) => person.id !== ownerId);
  const sharedWithUserIds = sharedWith.filter(
    (id) => id !== ownerId && people.some((person) => person.id === id),
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;
    if (!name.trim()) {
      toast.error("Enter an agent name.");
      return;
    }
    if (!ownerId) {
      toast.error("Select an owner.");
      return;
    }
    const id = hireAgent({ name, ownerUserId: ownerId, sharedWithUserIds });
    if (!id) {
      toast.error("Select an owner.");
      return;
    }
    toast.success(`${name.trim()} hired.`);
    navigate(agentsHref(`/agents/${id}`));
  };

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={agentsHref("/")}
          className="mb-2 inline-flex items-center gap-1 text-sm text-gray-600 hover:underline dark:text-gray-400"
        >
          <ArrowLeft className="size-4" aria-hidden />
          Agents
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Create an agent</h1>
        <p className="mt-1 max-w-2xl text-sm text-gray-600 dark:text-gray-400">
          Create as many agents as you need. Each one uses the shared credit pool.
        </p>
      </div>

      {viewOnly ? (
        <p className="max-w-2xl rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-100">
          Standard role is view only. Ask a Super Admin to add an agent.
        </p>
      ) : null}

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="text-base">New agent</CardTitle>
          <CardDescription>
            Name the agent and choose the owner. The owner is the person Overseer notifies.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Agent name</span>
              <Input
                className="mt-1 w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Riley Chen"
                disabled={!canCreate}
                autoComplete="off"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700 dark:text-gray-300">Owner</span>
              <select
                className="mt-1 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm disabled:opacity-50 dark:border-white/20 dark:bg-[#12161d] dark:text-white"
                value={ownerId}
                onChange={(e) => setOwnerChoice(e.target.value)}
                disabled={!canCreate || people.length === 0}
              >
                {people.length === 0 ? (
                  <option value="">No people in this organization</option>
                ) : null}
                {people.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name} ({person.email})
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-gray-500">
                The person Overseer notifies.
              </span>
            </label>
            <fieldset className="block text-sm" disabled={!canCreate}>
              <legend className="font-medium text-gray-700 dark:text-gray-300">Shared with</legend>
              <p className="mt-1 text-xs text-gray-500">
                People in your organization who can open this agent.
              </p>
              {shareOptions.length === 0 ? (
                <p className="mt-2 text-sm text-gray-500">Only the owner can open this agent.</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {shareOptions.map((person) => (
                    <label key={person.id} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        className="mt-1"
                        checked={sharedWithUserIds.includes(person.id)}
                        onChange={() =>
                          setSharedWith((current) =>
                            current.includes(person.id)
                              ? current.filter((id) => id !== person.id)
                              : [...current, person.id],
                          )
                        }
                      />
                      <span>
                        {person.name}
                        <span className="block text-xs text-gray-500">{person.email}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </fieldset>

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(agentsHref("/"))}>
                Cancel
              </Button>
              <Button type="submit" disabled={!canCreate}>
                Create agent
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
